import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

const SWIPER_VERSION = '12.1.3';
const SWIPER_BASE_URL = `https://cdn.jsdelivr.net/npm/swiper@${SWIPER_VERSION}`;

export class CarouselComponent extends Component {
  /** @type {any} */
  SwiperClass = null;

  /** @type {any[] | null} */
  SwiperModules = null;

  /** @type {any} */
  swiper = null;

  connectedCallback() {
    super.connectedCallback();

    const settingsEl = this.querySelector('script[type="application/json"]');
    const earlySettings = JSON.parse(settingsEl?.textContent || '{}');
    if (earlySettings.arrows === 'external' && earlySettings.externalRoot) {
      document.getElementById(earlySettings.externalRoot)?.classList.add('is-active');
    }

    requestIdleCallback(async () => {
      await this.loadSwiper();
      this.initSwiper();
    });
  }

  disconnectedCallback() {
    this.offCleanup?.();
    this.swiper?.destroy();
    super.disconnectedCallback();
  }

  async loadSwiper() {
    const windowObject = /** @type {any} */ (window);

    if (windowObject.Swiper && windowObject.SwiperModules) {
      this.SwiperClass = windowObject.Swiper;
      this.SwiperModules = windowObject.SwiperModules;
      return;
    }

    ['swiper', 'navigation', 'pagination', 'scrollbar', 'a11y'].forEach((name) => this.loadStyle(name));

    try {
      const moduleNames = ['navigation', 'pagination', 'scrollbar', 'autoplay', 'a11y', 'free-mode', 'mousewheel'];
      const [{ default: Swiper }, ...loadedModules] = await Promise.all([
        // @ts-ignore — dynamic import from CDN
        import(`${SWIPER_BASE_URL}/swiper.min.mjs`),
        // @ts-ignore — dynamic import from CDN
        ...moduleNames.map(
          // @ts-ignore — dynamic import from CDN
          (m) => import(`${SWIPER_BASE_URL}/modules/${m}.min.mjs`)
        ),
      ]);

      windowObject.Swiper = Swiper;
      windowObject.SwiperModules = loadedModules.map((m) => m.default);

      this.SwiperClass = windowObject.Swiper;
      this.SwiperModules = windowObject.SwiperModules;
    } catch (err) {
      console.error('Swiper load failed', err);
    }
  }

  /** @param {string} name */
  loadStyle(name) {
    const id = `swiper-${name}-css`;
    if (document.getElementById(id)) return;

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `${SWIPER_BASE_URL}/${name === 'swiper' ? '' : 'modules/'}${name}.min.css`;
    document.head.appendChild(link);
  }

  /**
   * Repeats the slides until there is enough track for `loop` to actually rotate.
   *
   * Swiper's loop mode works by moving slides from one end to the other, so it
   * needs materially more content than one screen: with six 280px cards on a
   * 1440px frame the track is 1800px against a 1440px viewport, Swiper computes
   * `loopedSlides: 1`, and the row advances once and then sits there — running,
   * unpaused, and motionless. A carousel authored as a continuous strip has no
   * way to know how many slides that will take, since it depends on the viewport.
   *
   * The copies are decorative: assistive tech has already read the originals, and
   * announcing them twice would imply twice as many of whatever they are.
   *
   * @param {HTMLElement} container
   * @param {Record<string, any>} settings
   */
  padForLoop(container, settings) {
    const wrapper = container.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    // Idempotent — this runs again on every theme-editor re-render, and cloning
    // the clones is how a row ends up with hundreds of slides.
    for (const stale of wrapper.querySelectorAll(':scope > [data-carousel-pad]')) stale.remove();

    const originals = Array.from(wrapper.children);
    if (!originals.length) return;

    const screens = settings.centeredSlides === true ? 3 : 2;
    const target = container.clientWidth * screens;
    for (let pass = 0; pass < 8 && wrapper.scrollWidth < target; pass += 1) {
      for (const original of originals) {
        const clone = /** @type {HTMLElement} */ (original.cloneNode(true));
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-carousel-pad', '');
        clone.removeAttribute('id');
        for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
        wrapper.appendChild(clone);
      }
    }
  }

  initSwiper() {
    const container = /** @type {HTMLElement | null} */ (this.querySelector('.swiper'));
    if (!container || !this.SwiperClass) return;

    this.swiper?.destroy();

    const settingsEl = this.querySelector('script[type="application/json"]');
    /** @type {Record<string, any>} */
    const settings = JSON.parse(settingsEl?.textContent || '{}');

    const arrows = settings.arrows || 'default';
    const externalRoot = settings.externalRoot
      ? document.getElementById(settings.externalRoot)
      : null;

    const mobileSlides = settings.slidesPerViewMobile ?? 1;
    const desktopSlides = settings.slidesPerViewDesktop ?? 4;
    const tabletSlides = settings.slidesPerViewTablet ?? Math.min(desktopSlides, mobileSlides + 1);
    const mobileGap = settings.gapMobile ?? 8;
    const desktopGap = settings.gapDesktop ?? 16;
    /* Centring is a per-breakpoint decision: a strip cut by both edges reads as
       continuous on a wide frame, but on a phone it hides the first slide's
       start behind the previous one. Defaults to whatever `centeredSlides` says,
       so no existing carousel moves. */
    const centeredDesktop = settings.centeredSlides === true;
    const centeredMobile = settings.centeredSlidesMobile ?? centeredDesktop;
    const paginationStyle = settings.paginationStyle || 'bullets';

    const autoWidth = settings.slidesPerView === 'auto';
    const freeMode = settings.freeMode === true;
    const offsetAfter = settings.slidesOffsetAfter ?? 0;
    const offsetBefore = settings.slidesOffsetBefore ?? 0;

    // Disable the carousel per breakpoint — Swiper toggles enabled on resize, and
    // we zero spaceBetween there so no slide margins remain in the static grid.
    const disableMobile = settings.disableMobile === true;
    const disableDesktop = settings.disableDesktop === true;
    const baseSpace = disableMobile ? 0 : mobileGap;
    const space750 = disableDesktop ? 0 : mobileGap;
    const space1024 = disableDesktop ? 0 : desktopGap;

    if (settings.loop) this.padForLoop(container, settings);

    this.swiper = new this.SwiperClass(container, {
      modules: this.SwiperModules,
      /* Swiper's a11y module defaults slideRole to 'group', which is not a permitted
         child of a list — it fails axe's `list` rule inside a <ul> wrapper and
         `aria-required-children` inside the role="list" wrapper Swiper adds.
         `"a11y": false` opts out entirely, for carousels whose slides are themselves
         links or buttons: any role stamped on those is wrong for the element. */
      a11y: settings.a11y === false ? { enabled: false } : { slideRole: 'listitem' },
      slideClass: settings.slideClass || 'swiper-slide',
      enabled: !disableMobile,
      freeMode,
      /* Opt-in wheel and trackpad paging: one card per gesture however hard it is
         flicked. `thresholdTime` is the cooldown between steps; the longer it is, the
         more of a Mac trackpad's momentum tail it absorbs — the tail keeps sending
         wheel events for up to a second after the fingers lift. `thresholdDelta`
         drops the 1-3px ticks that tail ends with; `releaseOnEdges` hands the scroll
         back to the panel the carousel sits in once the row is exhausted. Under
         `loop` there is no edge, so a vertical wheel over the row keeps cycling it
         and the panel behind only scrolls once the pointer leaves the row.
         `"horizontal"` is the free-scroll form for a freeMode row inside a page:
         only sideways trackpad gestures move it, so a vertical wheel over the row
         keeps scrolling the page. */
      mousewheel:
        settings.mousewheel === true
          ? { releaseOnEdges: true, thresholdTime: 500, thresholdDelta: 4 }
          : settings.mousewheel === 'horizontal'
            ? { forceToAxis: true, releaseOnEdges: true }
            : false,
      grabCursor: freeMode || settings.grabCursor === true,
      slidesPerView: autoWidth ? 'auto' : mobileSlides,
      spaceBetween: baseSpace,
      slidesOffsetAfter: offsetAfter,
      slidesOffsetBefore: offsetBefore,
      /* Opt-in, both of them. A row that advances on a timer has to wrap or it
         simply stops at the last slide, and `speed` is how long one advance
         takes — the default 300ms reads as a snap on a wide slide. Neither is
         touched unless a caller asks, so existing carousels are unchanged. */
      loop: settings.loop === true,
      ...(settings.speed ? { speed: settings.speed } : {}),
      /* Opt-in too: starts the track on a given slide instead of the first. A
         selector whose chosen item sits past the fold otherwise loads showing no
         selection at all. Swiper already defaults this to 0 and clamps to the end
         of the track, so omitting it leaves every existing carousel untouched. */
      ...(settings.initialSlide ? { initialSlide: settings.initialSlide } : {}),
      /* Also opt-in: it puts the active slide in the middle of the frame rather
         than flush to the start, so a strip is cut by BOTH edges instead of only
         the far one. Default false, so no existing carousel moves. */
      centeredSlides: centeredMobile === true,
      breakpoints: autoWidth
        ? {
            750: { spaceBetween: space750, enabled: !disableDesktop, centeredSlides: centeredDesktop },
            1024: { spaceBetween: space1024, enabled: !disableDesktop, centeredSlides: centeredDesktop },
          }
        : {
            750: {
              slidesPerView: tabletSlides,
              spaceBetween: space750,
              enabled: !disableDesktop,
              centeredSlides: centeredDesktop,
            },
            1024: {
              slidesPerView: desktopSlides,
              spaceBetween: space1024,
              enabled: !disableDesktop,
              centeredSlides: centeredDesktop,
            },
          },
      autoplay: settings.autoplay
        ? {
            delay: (settings.autoplayDelay ?? 5) * 1000,
            disableOnInteraction: false,
            // Opt-in: Swiper defaults this to false. Pairs with disableOnInteraction: false,
            // so hovering pauses and leaving resumes.
            pauseOnMouseEnter: settings.pauseOnHover === true,
          }
        : false,
      pagination: settings.pagination
        ? {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            ...(paginationStyle === 'numbers'
              ? {
                  renderBullet: (/** @type {number} */ index, /** @type {string} */ className) =>
                    `<span class="${className} custom-carousel__num">${String(index + 1).padStart(2, '0')}</span>`,
                }
              : {}),
          }
        : false,
      scrollbar: settings.scrollbar
        ? { el: this.querySelector('.swiper-scrollbar'), draggable: true }
        : false,
      /* Opt-in: an auto-width row ends on a stop at the very end of the track, which
         usually sits only a sliver past the stop before it. The arrows then spend a
         whole click on that sliver — the last card needs two clicks to show in full,
         and two more to step back. Dropping the second-to-last stop when the gap to
         the end is under half a slide makes one click land on the end. */
      ...(settings.mergeEndSnap === true
        ? {
            on: {
              slidesUpdated: (/** @type {any} */ swiper) => {
                const { snapGrid, slidesSizesGrid } = swiper;
                if (snapGrid.length < 3) return;
                const gap = snapGrid[snapGrid.length - 1] - snapGrid[snapGrid.length - 2];
                if (gap < (slidesSizesGrid[slidesSizesGrid.length - 1] ?? 0) / 2) {
                  snapGrid.splice(snapGrid.length - 2, 1);
                }
              },
            },
          }
        : {}),
      /* `prevEl`/`nextEl` let a carousel reuse the theme's own arrow markup
         (e.g. slideshow-arrows) instead of Swiper's `.swiper-button-*` defaults. */
      navigation:
        arrows === 'none'
          ? false
          : {
              nextEl:
                arrows === 'external' && externalRoot
                  ? externalRoot.querySelector('[data-carousel-arrow="next"]')
                  : this.querySelector(settings.nextEl || '.swiper-button-next'),
              prevEl:
                arrows === 'external' && externalRoot
                  ? externalRoot.querySelector('[data-carousel-arrow="prev"]')
                  : this.querySelector(settings.prevEl || '.swiper-button-prev'),
            },
    });

    /* slideRole: 'listitem' above only holds up if the wrapper is a list. Swiper adds
       role="list" for some wrappers but not all, and a listitem with no list parent
       fails aria-required-parent — so set it explicitly. Harmless on the <ul>
       wrappers, where it just restates the native role. */
    if (settings.a11y !== false) {
      this.swiper.wrapperEl?.setAttribute('role', 'list');
    }

    const setEdgeState = () => {
      if (!this.swiper) return;
      this.classList.toggle('is-beginning', this.swiper.isBeginning);
      this.classList.toggle('is-end', this.swiper.isEnd);
    };
    this.swiper.on('progress', setEdgeState);
    this.swiper.on('resize', setEdgeState);
    setEdgeState();

    // HUM-614: Set `inert` on non-visible slides so their interactive content
    // is excluded from the tab order. Updated on every slide change and on
    // breakpoint resize (when slidesPerView may change).
    const updateSlideInert = () => {
      if (!this.swiper || !this.swiper.slides) return;
      const { slides, activeIndex, params } = this.swiper;
      /* A carousel disabled at this breakpoint (`disableMobile`/`disableDesktop`) lays
         every slide out at once as a static grid, and Swiper reports no meaningful
         active window — so the visibility maths below would inert everything past the
         first slide and kill its links. Nothing is off-view here. */
      if (this.swiper.enabled === false) {
        for (const slide of slides) slide.removeAttribute('inert');
        return;
      }
      /* `slidesPerView: 'auto'` leaves params.slidesPerView as the string, so ask
         Swiper how many auto-width slides the viewport actually fits. Assuming 1
         made every visible slide but the active one `inert`, so their links and
         buttons stopped responding. Falls back to "all slides" rather than 1, so a
         missing method leaves the carousel interactive. */
      const dynamicPerView = this.swiper.slidesPerViewDynamic?.();
      const perView =
        typeof params.slidesPerView === 'number'
          ? Math.ceil(params.slidesPerView)
          : Math.max(1, Math.ceil(dynamicPerView || slides.length));
          
      const half = params.centeredSlides === true ? Math.floor((perView - 1) / 2) : 0;
      const first = activeIndex - half;
      for (let i = 0; i < slides.length; i++) {
        // Only write when the state actually flips — this runs on several Swiper
        // events, and a no-op toggleAttribute still counts as a DOM mutation.
        const hidden = i < first || i >= first + perView;
        if (hidden !== slides[i].hasAttribute('inert')) {
          slides[i].toggleAttribute('inert', hidden);
        }
      }
    };
    this.swiper.on('slideChangeTransitionEnd', updateSlideInert);
    this.swiper.on('resize', updateSlideInert);
    // Crossing a breakpoint is what flips `enabled`, so re-run there too.
    this.swiper.on('breakpoint', updateSlideInert);
    /* freeMode drags settle without a slide change, so transitionEnd is what clears
       `inert` from slides the user has scrolled into view. */
    this.swiper.on('transitionEnd', updateSlideInert);
    updateSlideInert();

    // Swiper doesn't expose a class for breakpoint-based `enabled`, so toggle our
    // own — CSS uses it to stack the slides when the carousel is off.
    this.offCleanup?.();
    if (disableMobile || disableDesktop) {
      const mq = window.matchMedia('(min-width: 750px)');
      const syncOff = () => {
        const off = mq.matches ? disableDesktop : disableMobile;
        this.classList.toggle('is-carousel-off', off);
      };
      syncOff();
      mq.addEventListener('change', syncOff);
      this.offCleanup = () => mq.removeEventListener('change', syncOff);
    } else {
      this.classList.remove('is-carousel-off');
      this.offCleanup = null;
    }

    if (settings.playPause) this.initPlayPause();
  }

  initPlayPause() {
    const button = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.custom-carousel__playpause')
    );
    const autoplay = this.swiper?.autoplay;
    if (!button || !autoplay) return;

    const setState = (/** @type {boolean} */ playing) => {
      button.dataset.playing = playing ? 'true' : 'false';
      button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      const label = playing ? button.dataset.labelPause : button.dataset.labelPlay;
      if (label) button.setAttribute('aria-label', label);
    };

    setState(!!autoplay.running);

    button.addEventListener('click', () => {
      if (autoplay.running) autoplay.stop();
      else autoplay.start();
      setState(!!autoplay.running);
    });

    this.swiper.on('autoplayStart', () => setState(true));
    this.swiper.on('autoplayStop', () => setState(false));
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}
